import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  type ColumnDef,
  getCoreRowModel,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { displayToastSuccess } from '@/helpers/display-toast';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { useDepartments } from '@/modules/settings/hooks/departments/use-departments';
import { useUpdateDepartments } from '@/modules/settings/hooks/departments/use-update-departments';
import { compareFormValues } from '@/utils/form-state.utils';

import { DeleteDepartmentModal } from '../../components/departments/delete-department-modal';
import { DepartmentsTable } from '../../components/departments/departments-table';
import { SettingsPageWrapper } from '../../components/settings-page-wrapper';
import { getDepartmentsColumns } from '../../constants/departments-table-columns';
import { DepartmentFormData } from '../../types/department';
import { SettingsOutletContext } from '../../types/settings-footer';
import {
  DepartmentsFormData,
  departmentsFormSchema,
} from '../../validations/department-validation';

let nextTempId = -1;

export const DepartmentsPage = () => {
  const { t } = useTranslation('authenticated.settings');
  const navigate = useNavigate();
  const { setFooterState } = useOutletContext<SettingsOutletContext>();

  const { data: departments, isLoading, error, refetch } = useDepartments();
  const updateDepartmentsMutation = useUpdateDepartments();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState<DepartmentFormData | null>(null);

  // Track initial data to compare against
  const initialDataRef = useRef<DepartmentFormData[]>([]);

  // Helper function to map departments from API to form data
  const mapDepartmentsToFormData = useCallback((deps: typeof departments): DepartmentFormData[] => {
    return deps
      ? deps.map(dept => ({
          departmentId: dept.id,
          name: dept.name,
          memberCount: dept.memberCount,
        }))
      : [];
  }, []);

  // Initialize form default values
  const defaultValues = useMemo(() => {
    return { departments: mapDepartmentsToFormData(departments) };
  }, [departments, mapDepartmentsToFormData]);

  const form = useForm<DepartmentsFormData>({
    resolver: zodResolver(departmentsFormSchema),
    mode: 'onChange',
    defaultValues,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'departments',
  });

  // Hook to guard against navigation when there are unsaved changes
  const unsavedChangesGuard = useUnsavedChangesGuard({
    isDirty: form.formState.isDirty,
    onDiscard: () => form.reset(),
  });

  // Store initial data when departments load
  useEffect(() => {
    if (departments) {
      const formData = mapDepartmentsToFormData(departments);
      initialDataRef.current = formData;
      form.reset({ departments: formData });
    }
  }, [departments, form, mapDepartmentsToFormData]);

  const handleSave = useCallback(async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    const formData = form.getValues();

    // Prepare departments for update (filter out empty names)
    const departmentsToUpdate = formData.departments
      .filter(dept => dept.name.trim().length > 0)
      .map(dept => ({
        id: dept.departmentId > 0 ? dept.departmentId : undefined,
        name: dept.name.trim(),
      }));

    try {
      await updateDepartmentsMutation.mutateAsync(departmentsToUpdate);
      // Refetch to get updated data with new IDs
      await refetch();
      displayToastSuccess(t('pages.departments.saveSuccess'));
    } catch {
      // Error handled by mutation
    }
  }, [form, updateDepartmentsMutation, refetch, t]);

  // Compare current state with initial using utility
  const hasActualChanges = useCallback(() => {
    const currentValues = form.getValues().departments;
    const initialValues = initialDataRef.current;
    return compareFormValues(currentValues, initialValues);
  }, [form]);

  // Update footer state - simple direct effect
  useEffect(() => {
    const hasChanges = hasActualChanges();
    const isValid = form.formState.isValid;

    setFooterState({
      isVisible: true,
      isEnabled: hasChanges && isValid,
      isLoading: updateDepartmentsMutation.isPending,
      onSave: handleSave,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.formState.isValid,
    form.formState.isDirty, // Trigger when form changes
    updateDepartmentsMutation.isPending,
    fields.length, // Trigger when departments added/removed
  ]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      setFooterState({
        isVisible: false,
        isEnabled: false,
        isLoading: false,
        onSave: () => {},
      });
    };
  }, [setFooterState]);

  const handleAddDepartment = () => {
    const newDepartment: DepartmentFormData = {
      departmentId: nextTempId--,
      name: '',
      memberCount: 0,
    };
    append(newDepartment);
  };

  const handleDeleteClick = useCallback(
    (department: DepartmentFormData, index: number) => {
      // For new departments (client-side only with negative IDs), just remove from form
      if (department.departmentId < 0) {
        remove(index);
        return;
      }

      // For existing departments, check if there are unsaved changes
      const shouldProceed = unsavedChangesGuard.checkUnsavedChanges(() => {
        // This callback executes after user discards changes
        setDepartmentToDelete(department);
        setDeleteModalOpen(true);
      });

      if (shouldProceed) {
        // Form is clean, proceed immediately with delete
        setDepartmentToDelete(department);
        setDeleteModalOpen(true);
      }
    },
    [remove, unsavedChangesGuard]
  );

  const handleDeleteSuccess = async () => {
    setDeleteModalOpen(false);
    setDepartmentToDelete(null);
    // Refetch data and reset form to clean state
    await refetch();
  };

  const columns = useMemo<ColumnDef<DepartmentFormData>[]>(
    () =>
      getDepartmentsColumns({
        onDelete: handleDeleteClick,
        control: form.control,
        t,
        navigate,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const table = useReactTable({
    data: fields,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  } as TableOptions<DepartmentFormData>);

  const unsavedChangesGuardProps = useMemo(
    () => ({
      isModalOpen: unsavedChangesGuard.isModalOpen,
      onDiscard: unsavedChangesGuard.handleModalDiscard,
      onCancel: unsavedChangesGuard.handleModalCancel,
    }),
    [
      unsavedChangesGuard.isModalOpen,
      unsavedChangesGuard.handleModalDiscard,
      unsavedChangesGuard.handleModalCancel,
    ]
  );

  return (
    <SettingsPageWrapper
      isLoading={isLoading}
      error={error}
      onRetry={() => {
        void refetch();
      }}
      unsavedChangesGuard={unsavedChangesGuardProps}
    >
      <Card className="p-6">
        {/* Card Header */}
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 className="text-lg font-semibold leading-7">{t('pages.departments.title')}</h2>
          <Button variant="ghost" className="hidden h-9 px-3 py-2 text-sm font-medium">
            {t('pages.departments.configureRates')}
          </Button>
        </div>
        <p className="mb-4 text-xs text-muted-foreground">{t('pages.departments.description')}</p>

        {/* Department Count */}
        <p className="mb-4 text-xs text-muted-foreground">
          {t('pages.departments.allDepartments', { count: departments?.length || 0 })}
        </p>

        {/* Table */}
        <Form {...form}>
          <form>
            <DepartmentsTable
              table={table}
              onAddDepartment={handleAddDepartment}
              hasDepartments={fields.length > 0}
              isLoading={isLoading}
            />
          </form>
        </Form>
      </Card>

      {/* Delete Department Modal */}
      <DeleteDepartmentModal
        department={departmentToDelete}
        allDepartments={departments || []}
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDepartmentToDelete(null);
        }}
        onSuccess={handleDeleteSuccess}
      />
    </SettingsPageWrapper>
  );
};
